import React, { useState, useEffect, useCallback, useRef } from 'react';
import { bioAPI } from '../services/api';
import useIsMobile from '../hooks/useIsMobile';

const LINK_ICONS = ['🎬','📸','🐦','💼','🌐','📧','📱','🛒','🎙️','✍️','📰','💡','🎵','📺','🔗','⭐','💰','📊'];
const CATEGORIES = ['Social', 'Content', 'Store', 'Contact', 'Newsletter', 'Other'];
const THEMES = [
  { color: '#6366f1', name: 'Indigo' },
  { color: '#10b981', name: 'Emerald' },
  { color: '#f59e0b', name: 'Amber' },
  { color: '#ec4899', name: 'Pink' },
  { color: '#3b82f6', name: 'Blue' },
  { color: '#ef4444', name: 'Red' },
  { color: '#8b5cf6', name: 'Purple' },
  { color: '#1e293b', name: 'Dark' },
];
const AVATAR_EMOJIS = ['🎬','📸','💡','🚀','🌟','🎙️','✍️','🎵','💼','🔥','🌈','🦋','🎯','💎','🌊','🎨'];

const emptyLink = { title: '', url: '', icon: '🔗', category: 'Social', isActive: true };

export default function BioPage() {
  const isMobile = useIsMobile();
  const [profile, setProfile]     = useState(null);
  const [hasProfile, setHasProfile] = useState(false);
  const [profileForm, setProfileForm] = useState({
    slug: '', displayName: '', bio: '', avatarEmoji: '🎬', themeColor: '#6366f1', isPublic: true,
  });
  const [slugStatus, setSlugStatus] = useState(null); // null | 'checking' | 'available' | 'taken'
  const [linkForm, setLinkForm]   = useState(emptyLink);
  const [editLinkId, setEditLinkId] = useState(null);
  const [showLinkForm, setShowLinkForm] = useState(false);
  const [view, setView]           = useState('manage'); // manage | preview | setup
  const [saving, setSaving]       = useState(false);
  const [savingLink, setSavingLink] = useState(false);
  const [error, setError]         = useState('');
  const [copied, setCopied]       = useState(false);
  const slugTimer = useRef(null);

  const load = useCallback(async () => {
    try {
      const res = await bioAPI.getMe();
      if (!res.data.empty) {
        setProfile(res.data);
        setHasProfile(true);
        setProfileForm({
          slug: res.data.slug || '',
          displayName: res.data.displayName || '',
          bio: res.data.bio || '',
          avatarEmoji: res.data.avatarEmoji || '🎬',
          themeColor: res.data.themeColor || '#6366f1',
          isPublic: res.data.isPublic !== false,
        });
      } else {
        setView('setup');
      }
    } catch {}
  }, []);

  useEffect(() => { load(); }, [load]);

  const checkSlug = (slug) => {
    if (!slug.trim()) return setSlugStatus(null);
    clearTimeout(slugTimer.current);
    setSlugStatus('checking');
    slugTimer.current = setTimeout(async () => {
      try {
        const res = await bioAPI.slugCheck(slug);
        setSlugStatus(res.data.available ? 'available' : 'taken');
      } catch { setSlugStatus(null); }
    }, 600);
  };

  const handleProfileSave = async () => {
    if (!profileForm.slug.trim()) return setError('Username (slug) is required.');
    if (slugStatus === 'taken') return setError('This username is already taken.');
    setError('');
    setSaving(true);
    try {
      const res = await bioAPI.saveProfile(profileForm);
      setProfile(res.data);
      setHasProfile(true);
      setView('manage');
      await load();
    } catch (e) {
      setError(e.response?.data?.error || 'Failed to save profile.');
    }
    setSaving(false);
  };

  const openAddLink = () => {
    setEditLinkId(null);
    setLinkForm(emptyLink);
    setShowLinkForm(true);
  };

  const openEditLink = (link) => {
    setEditLinkId(link.id);
    setLinkForm({ title: link.title, url: link.url, icon: link.icon || '🔗', category: link.category || 'Social', isActive: link.isActive !== false });
    setShowLinkForm(true);
  };

  const handleLinkSave = async () => {
    if (!linkForm.title.trim()) return setError('Link title is required.');
    if (!linkForm.url.trim()) return setError('URL is required.');
    setError('');
    setSavingLink(true);
    try {
      if (editLinkId) await bioAPI.updateLink(editLinkId, linkForm);
      else            await bioAPI.addLink(linkForm);
      setShowLinkForm(false);
      await load();
    } catch (e) {
      setError(e.response?.data?.error || 'Failed to save link.');
    }
    setSavingLink(false);
  };

  const handleToggle = async (id) => {
    try { await bioAPI.toggleLink(id); await load(); } catch {}
  };

  const handleDeleteLink = async (id) => {
    if (!window.confirm('Delete this link?')) return;
    try { await bioAPI.deleteLink(id); await load(); } catch {}
  };

  const publicUrl = profile ? `${window.location.origin}/bio/${profile.slug}` : '';
  const copyUrl = async () => {
    await navigator.clipboard.writeText(publicUrl);
    setCopied(true);
    setTimeout(() => setCopied(false), 2500);
  };

  const themeColor = profileForm.themeColor || '#6366f1';

  // ── SETUP / EDIT PROFILE VIEW ─────────────────────────────────
  if (view === 'setup' || view === 'editProfile') {
    return (
      <div style={{ padding: '2rem', maxWidth: 600, margin: '0 auto' }}>
        <div style={{ marginBottom: '2rem' }}>
          <h1 style={{ fontFamily: "'Syne', sans-serif", fontWeight: 800, fontSize: '1.8rem', margin: 0, color: 'var(--ink)' }}>
            🔗 {view === 'setup' ? 'Set Up Your Link-in-Bio' : 'Edit Bio Profile'}
          </h1>
          <p style={{ color: '#64748b', marginTop: '0.3rem', fontSize: '0.9rem' }}>
            {view === 'setup' ? 'Create your public page to share all your important links in one place' : 'Update your public bio profile'}
          </p>
        </div>

        <div style={{ background: 'white', borderRadius: 16, border: '1px solid #e2e8f0', padding: '2rem' }}>
          {/* Avatar Emoji */}
          <div style={{ marginBottom: '1.25rem' }}>
            <label style={lbl}>Avatar Emoji</label>
            <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
              {AVATAR_EMOJIS.map(e => (
                <button key={e} onClick={() => setProfileForm(p => ({ ...p, avatarEmoji: e }))}
                  style={{ width: 40, height: 40, borderRadius: 10, border: `2px solid ${profileForm.avatarEmoji === e ? themeColor : '#e2e8f0'}`,
                           background: profileForm.avatarEmoji === e ? themeColor + '15' : 'white',
                           fontSize: '1.2rem', cursor: 'pointer' }}>
                  {e}
                </button>
              ))}
            </div>
          </div>

          {/* Display Name */}
          <div style={{ marginBottom: '1rem' }}>
            <label style={lbl}>Display Name</label>
            <input value={profileForm.displayName} onChange={e => setProfileForm(p => ({ ...p, displayName: e.target.value }))}
              placeholder="e.g. Priya Sharma"
              style={inp} />
          </div>

          {/* Slug */}
          <div style={{ marginBottom: '1rem' }}>
            <label style={lbl}>Username / Page URL *</label>
            <div style={{ position: 'relative' }}>
              <span style={{ position: 'absolute', left: '0.75rem', top: '50%', transform: 'translateY(-50%)',
                             fontSize: '0.82rem', color: '#94a3b8', pointerEvents: 'none' }}>
                /bio/
              </span>
              <input value={profileForm.slug}
                onChange={e => {
                  const val = e.target.value.toLowerCase().replace(/[^a-z0-9_-]/g, '');
                  setProfileForm(p => ({ ...p, slug: val }));
                  checkSlug(val);
                }}
                placeholder="yourname"
                style={{ ...inp, paddingLeft: '3.5rem', borderColor: slugStatus === 'taken' ? '#ef4444' : slugStatus === 'available' ? '#10b981' : '#e2e8f0' }} />
            </div>
            {slugStatus === 'checking'  && <div style={{ fontSize: '0.73rem', color: '#94a3b8', marginTop: '0.25rem' }}>Checking…</div>}
            {slugStatus === 'available' && <div style={{ fontSize: '0.73rem', color: '#10b981', marginTop: '0.25rem' }}>✅ Available!</div>}
            {slugStatus === 'taken'     && <div style={{ fontSize: '0.73rem', color: '#ef4444', marginTop: '0.25rem' }}>❌ Already taken</div>}
          </div>

          {/* Bio */}
          <div style={{ marginBottom: '1rem' }}>
            <label style={lbl}>Bio / Tagline</label>
            <textarea value={profileForm.bio} onChange={e => setProfileForm(p => ({ ...p, bio: e.target.value }))}
              placeholder="Tech creator | 125K YouTube | Business enquiries 👇"
              rows={2} style={{ ...inp, resize: 'vertical' }} />
          </div>

          {/* Theme */}
          <div style={{ marginBottom: '1rem' }}>
            <label style={lbl}>Theme Color</label>
            <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
              {THEMES.map(t => (
                <button key={t.color} onClick={() => setProfileForm(p => ({ ...p, themeColor: t.color }))}
                  style={{ width: 32, height: 32, borderRadius: '50%', border: `3px solid ${profileForm.themeColor === t.color ? '#1e293b' : 'transparent'}`,
                           background: t.color, cursor: 'pointer', outline: 'none' }}
                  title={t.name} />
              ))}
            </div>
          </div>

          {/* Public toggle */}
          <div style={{ marginBottom: '1.5rem', display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
            <div onClick={() => setProfileForm(p => ({ ...p, isPublic: !p.isPublic }))}
              style={{ width: 44, height: 24, borderRadius: 12, background: profileForm.isPublic ? '#10b981' : '#e2e8f0',
                       cursor: 'pointer', position: 'relative', transition: 'background 0.2s' }}>
              <div style={{ position: 'absolute', top: 3, left: profileForm.isPublic ? 22 : 3, width: 18, height: 18,
                             borderRadius: '50%', background: 'white', transition: 'left 0.2s', boxShadow: '0 1px 3px rgba(0,0,0,0.2)' }} />
            </div>
            <span style={{ fontSize: '0.85rem', color: '#374151', fontWeight: 600 }}>
              {profileForm.isPublic ? '🌐 Page is Public' : '🔒 Page is Private'}
            </span>
          </div>

          {error && <div style={{ background: '#fee2e2', borderRadius: 8, padding: '0.6rem 0.9rem', fontSize: '0.82rem', color: '#991b1b', marginBottom: '0.75rem' }}>{error}</div>}

          <div style={{ display: 'flex', gap: '0.6rem' }}>
            <button onClick={handleProfileSave} disabled={saving}
              style={{ flex: 1, padding: '0.75rem', borderRadius: 10, border: 'none',
                       background: saving ? '#94a3b8' : 'var(--ink)', color: 'white',
                       fontWeight: 700, fontSize: '0.88rem', cursor: saving ? 'not-allowed' : 'pointer' }}>
              {saving ? 'Saving…' : 'Save Profile'}
            </button>
            {hasProfile && (
              <button onClick={() => setView('manage')}
                style={{ padding: '0.75rem 1.1rem', borderRadius: 10, border: '1px solid #e2e8f0',
                         background: 'white', color: '#64748b', fontWeight: 600, fontSize: '0.85rem', cursor: 'pointer' }}>
                Cancel
              </button>
            )}
          </div>
        </div>
      </div>
    );
  }

  // ── MANAGE VIEW ───────────────────────────────────────────────
  return (
    <div style={{ padding: '2rem', maxWidth: 900, margin: '0 auto' }}>
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '1.75rem' }}>
        <div>
          <h1 style={{ fontFamily: "'Syne', sans-serif", fontWeight: 800, fontSize: '1.8rem', margin: 0, color: 'var(--ink)' }}>
            🔗 Link-in-Bio
          </h1>
          <p style={{ color: '#64748b', marginTop: '0.3rem', fontSize: '0.9rem' }}>
            Manage your public page · Share one link everywhere
          </p>
        </div>
        <div style={{ display: 'flex', gap: '0.5rem' }}>
          <button onClick={() => setView('editProfile')}
            style={{ padding: '0.5rem 0.9rem', borderRadius: 8, border: '1px solid #e2e8f0', background: 'white',
                     color: '#64748b', fontWeight: 600, fontSize: '0.78rem', cursor: 'pointer' }}>
            ✏️ Edit Profile
          </button>
          <button onClick={() => setView(view === 'preview' ? 'manage' : 'preview')}
            style={{ padding: '0.5rem 0.9rem', borderRadius: 8, border: '1px solid #e2e8f0',
                     background: view === 'preview' ? 'var(--ink)' : 'white',
                     color: view === 'preview' ? 'white' : '#64748b',
                     fontWeight: 600, fontSize: '0.78rem', cursor: 'pointer' }}>
            👁 Preview
          </button>
          <button onClick={openAddLink}
            style={{ padding: '0.5rem 1rem', borderRadius: 8, border: 'none', background: 'var(--ink)',
                     color: 'white', fontWeight: 700, fontSize: '0.78rem', cursor: 'pointer' }}>
            + Add Link
          </button>
        </div>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: (!isMobile && view === 'preview') ? '1fr 360px' : '1fr', gap: '1.5rem' }}>
        {/* LEFT — Link List */}
        <div>
          {/* URL bar */}
          <div style={{ background: 'white', borderRadius: 12, border: '1px solid #e2e8f0',
                        padding: '0.85rem 1.25rem', marginBottom: '1.25rem',
                        display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
            <span style={{ fontSize: '0.8rem', color: '#64748b', flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontFamily: 'monospace' }}>
              🔗 {publicUrl}
            </span>
            <button onClick={copyUrl}
              style={{ padding: '0.4rem 0.85rem', borderRadius: 7, border: 'none', cursor: 'pointer',
                       background: copied ? '#10b981' : '#f1f5f9', color: copied ? 'white' : '#374151',
                       fontWeight: 600, fontSize: '0.75rem', whiteSpace: 'nowrap' }}>
              {copied ? '✅ Copied!' : '📋 Copy Link'}
            </button>
            <a href={publicUrl} target="_blank" rel="noreferrer"
              style={{ padding: '0.4rem 0.75rem', borderRadius: 7, border: '1px solid #e2e8f0',
                       background: 'white', color: '#374151', fontWeight: 600, fontSize: '0.75rem',
                       textDecoration: 'none', whiteSpace: 'nowrap' }}>
              ↗ Open
            </a>
          </div>

          {/* Stats row */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '0.6rem', marginBottom: '1.25rem' }}>
            {[
              { label: 'Total Links',   val: profile?.links?.length || 0 },
              { label: 'Active Links',  val: profile?.links?.filter(l => l.isActive !== false).length || 0 },
              { label: 'Total Clicks',  val: profile?.links?.reduce((s, l) => s + (l.clickCount || 0), 0) || 0 },
            ].map(s => (
              <div key={s.label} style={{ background: 'white', borderRadius: 10, border: '1px solid #e2e8f0', padding: '0.85rem 1rem' }}>
                <div style={{ fontSize: '1.4rem', fontWeight: 800, color: themeColor }}>{s.val}</div>
                <div style={{ fontSize: '0.72rem', color: '#64748b', fontWeight: 600 }}>{s.label}</div>
              </div>
            ))}
          </div>

          {/* Links */}
          {(!profile?.links || profile.links.length === 0) ? (
            <div style={{ background: 'white', borderRadius: 14, border: '2px dashed #e2e8f0',
                          padding: '3rem 2rem', textAlign: 'center', color: '#94a3b8' }}>
              <div style={{ fontSize: '2.5rem', marginBottom: '0.75rem' }}>🔗</div>
              <div style={{ fontWeight: 700, color: '#64748b' }}>No links yet</div>
              <div style={{ fontSize: '0.82rem', marginTop: '0.3rem' }}>Click "+ Add Link" to add your first link</div>
            </div>
          ) : (
            <div style={{ display: 'grid', gap: '0.6rem' }}>
              {profile.links.map(link => (
                <div key={link.id} style={{ background: 'white', borderRadius: 12, border: '1px solid #e2e8f0',
                                             padding: '0.9rem 1.1rem', display: 'flex', gap: '0.85rem', alignItems: 'center',
                                             opacity: link.isActive !== false ? 1 : 0.55 }}>
                  <div style={{ width: 40, height: 40, borderRadius: 10, background: themeColor + '15',
                                display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '1.2rem', flexShrink: 0 }}>
                    {link.icon || '🔗'}
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontWeight: 700, fontSize: '0.88rem', color: '#1e293b' }}>{link.title}</div>
                    <div style={{ fontSize: '0.73rem', color: '#94a3b8', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {link.url}
                    </div>
                  </div>
                  <div style={{ fontSize: '0.7rem', color: '#94a3b8', whiteSpace: 'nowrap' }}>
                    {link.clickCount || 0} clicks
                  </div>
                  {/* Toggle */}
                  <div onClick={() => handleToggle(link.id)}
                    style={{ width: 36, height: 20, borderRadius: 10, background: link.isActive !== false ? themeColor : '#e2e8f0',
                             cursor: 'pointer', position: 'relative', flexShrink: 0, transition: 'background 0.2s' }}>
                    <div style={{ position: 'absolute', top: 2, left: link.isActive !== false ? 18 : 2, width: 16, height: 16,
                                   borderRadius: '50%', background: 'white', transition: 'left 0.2s' }} />
                  </div>
                  <button onClick={() => openEditLink(link)}
                    style={{ padding: '0.35rem 0.6rem', borderRadius: 7, border: '1px solid #e2e8f0', background: 'white',
                             color: '#374151', fontWeight: 600, fontSize: '0.72rem', cursor: 'pointer', flexShrink: 0 }}>
                    ✏️
                  </button>
                  <button onClick={() => handleDeleteLink(link.id)}
                    style={{ padding: '0.35rem 0.6rem', borderRadius: 7, border: '1px solid #fee2e2', background: '#fff5f5',
                             color: '#ef4444', fontWeight: 600, fontSize: '0.72rem', cursor: 'pointer', flexShrink: 0 }}>
                    🗑
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* RIGHT — Phone Preview */}
        {view === 'preview' && (
          <div style={{ position: 'sticky', top: '1rem', height: 'fit-content' }}>
            <div style={{ fontSize: '0.75rem', fontWeight: 700, color: '#64748b', textAlign: 'center', marginBottom: '0.75rem', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
              Phone Preview
            </div>
            <div style={{ width: '100%', maxWidth: 340, margin: '0 auto', background: '#1e293b', borderRadius: 32,
                          padding: '12px', boxShadow: '0 20px 60px rgba(0,0,0,0.3)' }}>
              <div style={{ background: 'white', borderRadius: 24, overflow: 'hidden', minHeight: 580 }}>
                <BioPublicPreview profile={profile} themeColor={themeColor} />
              </div>
            </div>
          </div>
        )}
      </div>

      {/* LINK FORM MODAL */}
      {showLinkForm && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex',
                      alignItems: 'center', justifyContent: 'center', zIndex: 999 }}>
          <div style={{ background: 'white', borderRadius: 16, padding: '1.75rem', width: '100%', maxWidth: 440 }}>
            <div style={{ fontWeight: 800, fontSize: '1.1rem', marginBottom: '1.25rem', color: 'var(--ink)' }}>
              {editLinkId ? '✏️ Edit Link' : '+ Add Link'}
            </div>

            <div style={{ marginBottom: '0.85rem' }}>
              <label style={lbl}>Icon</label>
              <div style={{ display: 'flex', gap: '0.4rem', flexWrap: 'wrap', marginBottom: '0.25rem' }}>
                {LINK_ICONS.map(e => (
                  <button key={e} onClick={() => setLinkForm(p => ({ ...p, icon: e }))}
                    style={{ width: 34, height: 34, borderRadius: 8, border: `2px solid ${linkForm.icon === e ? themeColor : '#e2e8f0'}`,
                             background: linkForm.icon === e ? themeColor + '15' : 'white',
                             fontSize: '1rem', cursor: 'pointer' }}>
                    {e}
                  </button>
                ))}
              </div>
            </div>

            <div style={{ marginBottom: '0.85rem' }}>
              <label style={lbl}>Title *</label>
              <input value={linkForm.title} onChange={e => setLinkForm(p => ({ ...p, title: e.target.value }))}
                placeholder="e.g. My YouTube Channel" style={inp} />
            </div>
            <div style={{ marginBottom: '0.85rem' }}>
              <label style={lbl}>URL *</label>
              <input value={linkForm.url} onChange={e => setLinkForm(p => ({ ...p, url: e.target.value }))}
                placeholder="https://youtube.com/..." style={inp} />
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem', marginBottom: '1.1rem' }}>
              <div>
                <label style={lbl}>Category</label>
                <select value={linkForm.category} onChange={e => setLinkForm(p => ({ ...p, category: e.target.value }))}
                  style={{ ...inp, cursor: 'pointer' }}>
                  {CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'flex-end' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', paddingBottom: '0.1rem' }}>
                  <div onClick={() => setLinkForm(p => ({ ...p, isActive: !p.isActive }))}
                    style={{ width: 40, height: 22, borderRadius: 11, background: linkForm.isActive ? themeColor : '#e2e8f0',
                             cursor: 'pointer', position: 'relative', transition: 'background 0.2s', flexShrink: 0 }}>
                    <div style={{ position: 'absolute', top: 2, left: linkForm.isActive ? 20 : 2, width: 18, height: 18,
                                   borderRadius: '50%', background: 'white', transition: 'left 0.2s' }} />
                  </div>
                  <span style={{ fontSize: '0.8rem', fontWeight: 600, color: '#374151' }}>
                    {linkForm.isActive ? 'Active' : 'Hidden'}
                  </span>
                </div>
              </div>
            </div>

            {error && <div style={{ background: '#fee2e2', borderRadius: 8, padding: '0.6rem', fontSize: '0.8rem', color: '#991b1b', marginBottom: '0.75rem' }}>{error}</div>}

            <div style={{ display: 'flex', gap: '0.6rem' }}>
              <button onClick={handleLinkSave} disabled={savingLink}
                style={{ flex: 1, padding: '0.7rem', borderRadius: 9, border: 'none',
                         background: savingLink ? '#94a3b8' : 'var(--ink)', color: 'white',
                         fontWeight: 700, fontSize: '0.85rem', cursor: savingLink ? 'not-allowed' : 'pointer' }}>
                {savingLink ? 'Saving…' : editLinkId ? 'Update Link' : 'Add Link'}
              </button>
              <button onClick={() => { setShowLinkForm(false); setError(''); }}
                style={{ padding: '0.7rem 1rem', borderRadius: 9, border: '1px solid #e2e8f0',
                         background: 'white', color: '#64748b', fontWeight: 600, fontSize: '0.85rem', cursor: 'pointer' }}>
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ── Phone Preview Component ───────────────────────────────────
function BioPublicPreview({ profile, themeColor }) {
  if (!profile) return null;
  const activeLinks = profile.links?.filter(l => l.isActive !== false) || [];
  return (
    <div style={{ minHeight: 580, background: '#f8fafc' }}>
      {/* Header */}
      <div style={{ background: themeColor, padding: '2rem 1.25rem 1.5rem', textAlign: 'center' }}>
        <div style={{ width: 64, height: 64, borderRadius: '50%', background: 'rgba(255,255,255,0.2)',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      fontSize: '2rem', margin: '0 auto 0.75rem' }}>
          {profile.avatarEmoji || '🎬'}
        </div>
        <div style={{ color: 'white', fontWeight: 800, fontSize: '1.1rem', marginBottom: '0.3rem' }}>
          {profile.displayName || profile.slug}
        </div>
        {profile.bio && (
          <div style={{ color: 'rgba(255,255,255,0.8)', fontSize: '0.75rem', lineHeight: 1.45 }}>
            {profile.bio}
          </div>
        )}
      </div>
      {/* Links */}
      <div style={{ padding: '1rem 1rem' }}>
        {activeLinks.map(link => (
          <a key={link.id} href={link.url} target="_blank" rel="noreferrer"
            style={{ display: 'flex', alignItems: 'center', gap: '0.65rem', padding: '0.7rem 0.9rem',
                     borderRadius: 12, background: 'white', border: '1px solid #e2e8f0',
                     marginBottom: '0.6rem', textDecoration: 'none', color: '#1e293b',
                     boxShadow: '0 1px 3px rgba(0,0,0,0.06)' }}>
            <span style={{ fontSize: '1.1rem' }}>{link.icon || '🔗'}</span>
            <span style={{ fontWeight: 700, fontSize: '0.85rem', flex: 1 }}>{link.title}</span>
            <span style={{ fontSize: '0.7rem', color: themeColor }}>→</span>
          </a>
        ))}
        {activeLinks.length === 0 && (
          <div style={{ textAlign: 'center', color: '#94a3b8', fontSize: '0.8rem', padding: '2rem 0' }}>
            No active links yet
          </div>
        )}
      </div>
      {/* Footer */}
      <div style={{ textAlign: 'center', padding: '0.75rem', fontSize: '0.65rem', color: '#cbd5e1' }}>
        Made with CreatorKhata
      </div>
    </div>
  );
}

const lbl = { fontSize: '0.73rem', fontWeight: 700, color: '#64748b', display: 'block', marginBottom: '0.3rem', textTransform: 'uppercase', letterSpacing: '0.03em' };
const inp = { width: '100%', padding: '0.55rem 0.75rem', borderRadius: 8, border: '1px solid #e2e8f0', fontSize: '0.83rem', outline: 'none', boxSizing: 'border-box' };
